import { Component, Input, OnChanges } from '@angular/core';
import {
  RetentionPolicyChip,
  RetentionPolicyDetails, RetentionPolicyUtils
} from '@app/shared/models/retention-policy';
import { FileUploadStatus } from "@shared/constants/file-manager/file-upload-status";
import { featureFlags } from "@environments/environment";

@Component({
    selector: 'app-retention-policy-chip',
    templateUrl: './retention-policy-chip.component.html',
    styleUrls: ['./retention-policy-chip.component.css'],
    standalone: false
})
export class RetentionPolicyChipComponent implements OnChanges {
  @Input({ required: true }) retentionPolicy?: RetentionPolicyChip;

  protected readonly uploadStatus = FileUploadStatus;
  expiration: string | undefined;
  alternativeText: string | undefined;
  expired: boolean = false;
  protected readonly suffix: string = "Expiring";
  currentDate: Date = new Date();
  threshold: Date = new Date();
  thresholdOffset: number = 0;
  utils = RetentionPolicyUtils;
  protected readonly featureFlags = featureFlags;

  private readonly INITIAL_EXPIRATION_DATE = new Date('2025-09-14T00:00:00Z');

  ngOnChanges(): void {
    if (this.retentionPolicy) {
      const { type, date, nonExpiringText, expirationDate } = this.retentionPolicy;
      const policyDetails = RetentionPolicyDetails[type];

      if (!policyDetails) {
        return;
      }

      this.currentDate = this.utils.getToday();

      const { expirationMonths, thresholdDays } = policyDetails;
      this.thresholdOffset = thresholdDays + this.getThresholdOffset();

      let finalExpirationDate = expirationDate == null
        ? this.getExpirationDate(new Date(date), expirationMonths)
        : new Date(expirationDate);

      const expirationDateToUse = this.expirationDateToUse(finalExpirationDate);

      this.threshold = new Date(this.currentDate);
      this.threshold.setDate(this.currentDate.getDate() + this.thresholdOffset);
      this.threshold.setHours(23, 59, 59, 999);

      if (!this.utils.isExpiringSoon(expirationDateToUse, this.threshold)) {
        if (nonExpiringText) {
          this.alternativeText = nonExpiringText;
        }
        return;
      }

      if (featureFlags.retentionPolicyJobActive && this.utils.isExpired(expirationDateToUse, this.currentDate)) {
        this.expired = true;
        this.expiration = this.getNextSunday(this.currentDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short'
        });
        return;
      }


      this.expiration = this.getNextSunday(expirationDateToUse).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short'
      });

      if (this.utils.isExpired(expirationDateToUse, this.currentDate)) {
        this.expired = true;
        this.expiration = "Expired";
      }
    }
  }

  private getExpirationDate(inputDate: Date, expirationMonths: number): Date {
    const expirationDate = new Date(inputDate);
    expirationDate.setMonth(expirationDate.getMonth() + expirationMonths);
    return expirationDate;
  }

  private getThresholdOffset(): number {
    const now = this.currentDate.getMonth();
    const daysInMonth = this.getDaysInMonth(this.currentDate.getFullYear(), now);
    return daysInMonth % 30 > 0 ? 1 : 0;
  }

  private getDaysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  protected getExpiringPrefix() {
    return this.suffix;
  }

  protected getExpiration()  {
    return this.expiration;
  }

   private expirationDateToUse(expirationDate: Date): Date {
    if (!featureFlags.retentionPolicyJobActive) return expirationDate;
    return expirationDate < this.INITIAL_EXPIRATION_DATE ? this.INITIAL_EXPIRATION_DATE : expirationDate;
  }

    private getNextSunday(date: Date): Date {
    const returnDate = new Date(date);
    returnDate.setDate(returnDate.getDate() + (7 - returnDate.getDay()) % 7);

    returnDate.setHours(0, 0, 0, 0);
    return returnDate;
  }
}
